import { ArrowUpRight, Check } from "lucide-react";

const BENEFITS = [
  {
    strong: "−50 % sur votre pack",
    rest: "(Essential, Évolution ou Premium)",
  },
  {
    strong: "Support prioritaire 6 mois",
    rest: "après la mise en ligne",
  },
  {
    strong: "Étude de cas dédiée",
    rest: "publiée sur le site avec votre accord",
  },
  {
    strong: "Un témoignage sincère",
    rest: "à l'issue du projet — c'est tout ce que je demande en retour",
    swapOrder: true,
  },
];

const TOTAL_SEATS = 3;
const SEATS_USED = 0; // À mettre à jour quand des places sont prises

export default function FounderOffer() {
  const seatsLeft = TOTAL_SEATS - SEATS_USED;

  return (
    <section
      data-testid="founder-section"
      className="relative overflow-hidden bg-[#F3F0FF] py-20 md:py-28"
    >
      {/* Aurora soft glows */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-32 -right-24 h-[440px] w-[440px] rounded-full"
        style={{
          background:
            "radial-gradient(circle, rgba(236,72,153,0.18), transparent 65%)",
        }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-32 -left-24 h-[440px] w-[440px] rounded-full"
        style={{
          background:
            "radial-gradient(circle, rgba(99,102,241,0.18), transparent 65%)",
        }}
      />

      <div className="relative z-10 mx-auto max-w-6xl px-5 md:px-10">
        <div className="grid items-center gap-12 md:gap-16 md:grid-cols-[1.2fr_1fr]">
          {/* LEFT — Pitch */}
          <div>
            <div
              data-testid="founder-eyebrow"
              className="mb-6 inline-flex items-center gap-2.5 text-[11px] font-bold uppercase tracking-[0.28em] text-[#A855F7]"
            >
              <span
                className="inline-block h-1.5 w-1.5 rounded-full animate-pulse"
                style={{
                  background:
                    "linear-gradient(135deg, #6366F1, #EC4899)",
                }}
              />
              Offre fondateur · Places limitées
            </div>

            <h2
              data-testid="founder-title"
              className="font-display text-[40px] md:text-[54px] leading-[1.05] tracking-[-0.02em] text-[#0B1027]"
            >
              Numedia ouvre ses portes,
              <br />
              <em
                className="not-italic font-normal italic"
                style={{
                  background:
                    "linear-gradient(90deg, #6366F1, #A855F7, #EC4899)",
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  color: "transparent",
                }}
              >
                et c'est maintenant.
              </em>
            </h2>

            <p
              data-testid="founder-lead"
              className="mt-6 max-w-xl text-base md:text-[17px] leading-relaxed text-[#0B1027]/70"
            >
              Numedia est un studio neuf. Plutôt que de vous montrer des
              témoignages génériques, je préfère vous proposer un vrai pacte de
              confiance. Devenez l'un des trois prochains clients du programme
              fondateur.
            </p>

            {/* Benefits */}
            <ul
              data-testid="founder-benefits"
              className="mt-8 mb-9 space-y-3"
            >
              {BENEFITS.map((b) => (
                <li
                  key={b.strong}
                  className="flex items-start gap-3.5 text-[15px] text-[#0B1027] leading-snug"
                >
                  <span className="mt-0.5 inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-white text-[#A855F7] relative">
                    <Check
                      className="h-3.5 w-3.5"
                      strokeWidth={2.8}
                      aria-hidden="true"
                    />
                    <span
                      aria-hidden="true"
                      className="pointer-events-none absolute inset-0 rounded-full"
                      style={{
                        padding: "1.5px",
                        background:
                          "linear-gradient(135deg,#6366F1,#A855F7,#EC4899)",
                        WebkitMask:
                          "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
                        WebkitMaskComposite: "xor",
                        maskComposite: "exclude",
                      }}
                    />
                  </span>
                  <span>
                    {b.swapOrder ? (
                      <>
                        En échange : <strong className="founder-grad-text">{b.strong}</strong>{" "}
                        {b.rest}
                      </>
                    ) : (
                      <>
                        <strong className="founder-grad-text">
                          {b.strong}
                        </strong>{" "}
                        {b.rest}
                      </>
                    )}
                  </span>
                </li>
              ))}
            </ul>

            {/* CTA + seats */}
            <div className="flex flex-wrap items-center gap-5">
              <a
                data-testid="founder-cta"
                href="#contact"
                className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-[15px] font-semibold text-white shadow-[0_10px_24px_rgba(168,85,247,0.32)] hover:translate-y-[-2px] transition-transform"
                style={{
                  background:
                    "linear-gradient(135deg, #6366F1, #A855F7, #EC4899)",
                }}
              >
                Réserver ma place
                <ArrowUpRight className="h-4 w-4" strokeWidth={2.4} />
              </a>

              <span
                data-testid="founder-seats"
                className="text-sm text-[#0B1027]/65 flex items-center gap-2"
              >
                Places restantes
                <span className="inline-flex gap-1">
                  {Array.from({ length: TOTAL_SEATS }).map((_, i) => (
                    <span
                      key={`seat-${i}`}
                      className="h-2 w-2 rounded-full"
                      style={{
                        background:
                          i < seatsLeft
                            ? "linear-gradient(135deg, #6366F1, #EC4899)"
                            : "rgba(11,16,39,0.15)",
                        boxShadow:
                          i < seatsLeft
                            ? "0 2px 6px rgba(168,85,247,0.35)"
                            : "none",
                      }}
                    />
                  ))}
                </span>
                <strong className="ml-1 text-[#0B1027]">
                  {seatsLeft} / {TOTAL_SEATS}
                </strong>
              </span>
            </div>

            <p className="mt-5 text-xs italic text-[#0B1027]/50">
              Offre valable jusqu'à attribution des {TOTAL_SEATS} places · Aucune
              souscription, aucun engagement caché.
            </p>
          </div>

          {/* RIGHT — Signed promise card */}
          <div
            data-testid="founder-promise-card"
            className="relative overflow-hidden rounded-[28px] p-8 md:p-9 text-white shadow-[0_20px_50px_rgba(11,16,39,0.18)]"
            style={{
              background:
                "linear-gradient(160deg, #0B1027 0%, #1a1442 50%, #2a1855 100%)",
            }}
          >
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-0"
              style={{
                background:
                  "radial-gradient(ellipse 70% 50% at 110% 20%, rgba(236,72,153,0.35), transparent 60%), radial-gradient(ellipse 70% 50% at -10% 110%, rgba(99,102,241,0.35), transparent 60%)",
              }}
            />

            {/* Top right "Engagement signé" pin */}
            <span className="absolute top-7 right-7 inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-white/10 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.18em] backdrop-blur-md z-10">
              <span
                className="h-1.5 w-1.5 rounded-full animate-pulse"
                style={{
                  background: "#34D399",
                  boxShadow: "0 0 8px #34D399",
                }}
              />
              Engagement signé
            </span>

            <div className="relative z-[5] pt-8 md:pt-12">
              <p className="font-display italic text-[18px] md:text-[19px] leading-[1.55] mb-8 mt-2">
                <span
                  className="font-display"
                  style={{
                    fontSize: "48px",
                    lineHeight: 0,
                    color: "rgba(236,72,153,0.5)",
                    marginRight: "6px",
                    verticalAlign: "-16px",
                  }}
                >
                  &ldquo;
                </span>
                Je m'engage à concevoir votre site comme si c'était le mien :
                avec une écoute réelle, un design sur mesure, et une livraison
                claire et tenue. Si je n'y parviens pas, je vous rembourse.
              </p>

              <div className="flex items-center gap-3.5 border-t border-white/10 pt-5">
                <div
                  className="flex h-12 w-12 items-center justify-center rounded-full font-display text-[20px] font-semibold text-white"
                  style={{
                    background:
                      "linear-gradient(135deg, #6366F1, #A855F7, #EC4899)",
                  }}
                >
                  DD
                </div>
                <div>
                  <div className="text-[15px] font-semibold">David Devos</div>
                  <div className="text-[12px] text-white/55">
                    Fondateur · Numedia Studio
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Local helper for gradient text */}
      <style>{`
        .founder-grad-text {
          background: linear-gradient(135deg, #6366F1, #A855F7, #EC4899);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
          font-weight: 700;
        }
      `}</style>
    </section>
  );
}
